import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TourService } from './tour.service';

/** Retângulo do alvo em coordenadas de viewport (o farol é `position: fixed`). */
interface RetanguloAlvo {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * D49/D50/D57 — FAROL do tour, no lugar da máscara.
 *
 * A mecânica anterior era um scrim preto a 80 % (`pointer-events: all`) + `body{overflow:hidden}`,
 * e o realce consistia em ERGUER o alvo acima dele por `z-index`. Como `z-index` é resolvido
 * dentro do contexto de empilhamento do ancestral, cada alvo novo trazia um bug próprio ("o botão
 * está sob o scrim") e uma regra CSS de exceção — foram 6 (`[data-tour].tour-focus`,
 * `[data-tour].tour-active`, `.flex-action:has(...)`, `.one-footer-bar:has(...)`,
 * `cdk-virtual-scroll-viewport`, `body.tour-ativo .modal-overlay`…), todas apagadas com a fatia
 * D49/D50.
 *
 * Aqui o realce é desenhado POR CIMA do retângulo do alvo, `pointer-events: none`: não cobre a
 * tela, não intercepta clique, não depende do empilhamento do alvo e vale igual para alvo por
 * seletor e por slug. Sob SSR o `TourService` nunca publica `alvoAtivo` (guardado por
 * `ehNavegador` em `acenderSeletor`), então `alvoVisivel`/`retanguloAlvo` também nunca emitem
 * algo não-nulo lá — este componente não pinta nada, mesmo sem checar plataforma sozinho.
 *
 * D57 — a MEDIÇÃO (isConnected/retângulo/ResizeObserver/scroll/resize) não vive mais aqui: saiu
 * para `ObservadorDeAlvo` (`tour-alvo-visivel.ts`), interno ao `TourService`, e este componente
 * só CONSOME `alvoVisivel`/`retanguloAlvo` — o mesmo veredito que `TourGuiaComponent` usa para
 * ancorar o cartão. Farol e cartão nunca mais divergem sobre "há alvo visível?".
 */
@Component({
  selector: 'tour-realce',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (retangulo(); as r) {
      <div
        class="farol"
        aria-hidden="true"
        [style.top.px]="r.top"
        [style.left.px]="r.left"
        [style.width.px]="r.width"
        [style.height.px]="r.height"
      ></div>
    }
  `,
  styles: [
    `
      .farol {
        position: fixed;
        pointer-events: none;
        z-index: 10000;
        border-radius: 8px;
        /* Fallback OBRIGATORIO: color-mix invalida a declaracao INTEIRA num browser que nao o
           suporte — sem esta linha antes, o farol nao teria nem o anel de 2 px, e o tour ficaria
           sem realce nenhum em silencio. Com ela, o pior caso e perder so o brilho externo. */
        box-shadow: 0 0 0 2px var(--main-200);
        box-shadow:
          0 0 0 2px var(--main-200),
          0 0 0 8px color-mix(in srgb, var(--main-200) 18%, transparent);
        animation: farol-pulso 1.2s ease-out 3;
      }

      /* Tres pulsos e depois acalma: o estado final do keyframe e igual ao da regra acima, entao
         a animacao termina sem "forwards" e sem salto visual. Mesmo fallback por keyframe: se o
         color-mix cair, o anel de 2 px continua pulsando sozinho em vez de sumir. */
      @keyframes farol-pulso {
        0% {
          box-shadow: 0 0 0 2px var(--main-200);
          box-shadow:
            0 0 0 2px var(--main-200),
            0 0 0 2px color-mix(in srgb, var(--main-200) 30%, transparent);
        }
        55% {
          box-shadow: 0 0 0 2px var(--main-200);
          box-shadow:
            0 0 0 2px var(--main-200),
            0 0 0 14px color-mix(in srgb, var(--main-200) 0%, transparent);
        }
        100% {
          box-shadow: 0 0 0 2px var(--main-200);
          box-shadow:
            0 0 0 2px var(--main-200),
            0 0 0 8px color-mix(in srgb, var(--main-200) 18%, transparent);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .farol {
          animation: none;
        }
      }
    `,
  ],
})
export class TourRealceComponent implements OnInit, OnDestroy {
  private readonly tour = inject(TourService);

  readonly retangulo = signal<RetanguloAlvo | null>(null);

  private assinaturaVisivel?: Subscription;
  private assinaturaRetangulo?: Subscription;
  /** Só para disparar o `scrollIntoView` uma vez por troca de alvo (não a cada remedição). */
  private alvoAnterior: HTMLElement | null = null;

  ngOnInit(): void {
    this.assinaturaVisivel = this.tour.alvoVisivel.subscribe((alvo) => this.aoTrocarVisivel(alvo));
    this.assinaturaRetangulo = this.tour.retanguloAlvo.subscribe((r) =>
      this.retangulo.set(r ? { top: r.top, left: r.left, width: r.width, height: r.height } : null),
    );
  }

  ngOnDestroy(): void {
    this.assinaturaVisivel?.unsubscribe();
    this.assinaturaRetangulo?.unsubscribe();
  }

  private aoTrocarVisivel(alvo: HTMLElement | null): void {
    if (alvo === this.alvoAnterior) return;
    this.alvoAnterior = alvo;
    if (alvo) {
      alvo.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }
}
